import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Users, Calendar, MoreVertical } from 'lucide-react';

/**
 * AdSetNode - Visual node for ad set in funnel builder
 */
const AdSetNode = memo(({ data, selected }) => {
    const {
        name = 'New Ad Set',
        parentCampaignId,
        targeting = {},
        schedule = {},
        status = 'draft',
        onEdit,
        onMenuClick
    } = data;

    // Format targeting summary
    const getTargetingSummary = () => {
        const parts = [];

        if (targeting.ageMin || targeting.ageMax) {
            parts.push(`${targeting.ageMin || 18}-${targeting.ageMax || 65}`);
        }

        if (targeting.genders?.length) {
            const genderMap = { male: 'M', female: 'F' };
            parts.push(targeting.genders.map(g => genderMap[g] || g).join('/'));
        }

        if (targeting.locations?.length) {
            parts.push(targeting.locations.slice(0, 2).join(', '));
            if (targeting.locations.length > 2) {
                parts.push(`+${targeting.locations.length - 2}`);
            }
        }

        return parts.join(' • ') || 'No targeting set';
    };

    // Format interests summary
    const getInterestsSummary = () => {
        if (!targeting.interests?.length) return null;
        const display = targeting.interests.slice(0, 3);
        const remaining = targeting.interests.length - 3;
        return (
            <span>
                {display.join(', ')}
                {remaining > 0 && <span style={{ color: 'var(--color-text-tertiary)' }}> +{remaining}</span>}
            </span>
        );
    };

    // Format schedule summary
    const getScheduleSummary = () => {
        if (!schedule.start) return 'No schedule';
        const start = new Date(schedule.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const end = schedule.end
            ? new Date(schedule.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : 'Ongoing';
        return `${start} - ${end}`;
    };

    const statusColors = {
        draft: 'var(--color-text-tertiary)',
        pending: 'var(--color-warning)',
        active: 'var(--color-success)',
        paused: 'var(--color-error)'
    };

    return (
        <div
            style={{
                background: 'var(--color-bg-primary)',
                border: selected
                    ? '2px solid var(--color-info)'
                    : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-3)',
                minWidth: '240px',
                boxShadow: selected ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
                transition: 'all var(--transition-fast)'
            }}
        >
            {/* Target handle (top) - connects from Campaign */}
            <Handle
                type="target"
                position={Position.Top}
                id="campaign-in"
                style={{
                    background: 'var(--color-primary)',
                    width: '10px',
                    height: '10px'
                }}
            />

            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-2)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--color-info-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Users size={14} style={{ color: 'var(--color-info)' }} />
                    </div>
                    <div>
                        <div style={{
                            fontSize: '10px',
                            color: 'var(--color-text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Ad Set
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {name}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    {/* Status indicator */}
                    <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: statusColors[status]
                    }} />

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onMenuClick?.();
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            borderRadius: 'var(--radius-sm)',
                            color: 'var(--color-text-tertiary)',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <MoreVertical size={14} />
                    </button>
                </div>
            </div>

            {/* Targeting info */}
            <div style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--space-2)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)'
            }}>
                <Users size={12} style={{ color: 'var(--color-text-tertiary)' }} />
                {getTargetingSummary()}
            </div>

            {/* Interests */}
            {getInterestsSummary() && (
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-2)',
                    padding: '4px 8px',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-sm)'
                }}>
                    {getInterestsSummary()}
                </div>
            )}

            {/* Schedule */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)'
            }}>
                <Calendar size={12} />
                {getScheduleSummary()}
            </div>

            {/* Source handle (bottom) - connects to Ads */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="ad-out"
                style={{
                    background: 'var(--color-info)',
                    width: '10px',
                    height: '10px'
                }}
            />
        </div>
    );
});

AdSetNode.displayName = 'AdSetNode';

export default AdSetNode;
